// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import TerceraComboBoxTemplate from '@front/templates/TerceraComboBoxTemplate.html';
import { Control } from './Control';
import { Resources } from '@shared/localizations/Resources';
import { ResourcesManager } from '@shared/commons/properties/ResourcesManager';
export class TerceraComboBox extends Control {
    constructor(options) {
        super(options);
        this.menuItems = null;
        this.private_OnMenuItemSelected = this.private_OnMenuItemSelected.bind(this);
    }
    getType() {
        return 'TerceraComboBox';
    }
    get width() {
        return this.find('*').clientWidth;
    }
    get maxWidth() {
        return null;
    }
    oninit() {
        super.oninit();
        this.observe('items', this.onItemsChanged);
        this.observe('selectedItem', this.onSelectedItemChanged);
        ResourcesManager.onLocaleChange.Subscribe(this.onLocaleChanged, this);
    }
    onteardown() {
        ResourcesManager.onLocaleChange.UnSubscribe(this.onLocaleChanged, this);
        const items = this.get('items');
        items.forEach((item) => {
            if (item.event === this.private_OnMenuItemSelected) {
                item.event = null;
            }
        });
        super.onteardown();
    }
    onItemsChanged(newItems) {
        if (!isValidArray(newItems)) {
            void this.set({ selectedItem: null, items: [] });
            return;
        }
        let newSelItem = null;
        const showLastValue = this.get('showLastValue');
        if (showLastValue) {
            const oldSelItem = this.get('selectedItem');
            newSelItem = !isNullOrUndefined(oldSelItem) ? this.getItemByValue(oldSelItem.value) : null;
        }
        if (isNullOrUndefined(newSelItem)) {
            newSelItem = newItems[0];
        }
        void this.set({ selectedItem: newSelItem });
        this.menuItems = this.updateMenuItems(newSelItem);
    }
    onSelectedItemChanged(newValue, oldValue) {
        this.updateSelection(newValue);
        if (!isNullOrUndefined(newValue) && !isNullOrUndefined(oldValue) && newValue.value === oldValue.value) {
            return;
        }
        const val = !isNullOrUndefined(newValue) ? newValue.value : null;
        this.fire(TerceraComboBoxEvents.ValueChange, val);
    }
    getItemByValue(value) {
        const items = this.get('items');
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.value === value) {
                return item;
            }
        }
        return null;
    }
    onClick(event) {
        const isEnabled = this.get('enabled');
        if (!isEnabled) {
            return;
        }
        const selectedItem = this.get('selectedItem');
        if (isNullOrUndefined(selectedItem) && !this.get('canHaveNoSelectedItem')) {
            return;
        }
        if (this.get('isMenuShown')) {
            event.original.stopPropagation();
            this.HideMenu();
            return;
        }
        this.MenuShow();
    }
    MenuShow() {
        const element = this.find('*');
        const rect = element.getBoundingClientRect();
        this.set('isMenuShown', true);
        const topBottomPadding = processNaN(parseInt(getCSSVariableValue('--padding-sm')), 2);
        const elementHeight = processNaN(parseInt(getCSSVariableValue('--height-md')), 32);
        showContextMenu({
            x: rect.x,
            y: rect.bottom,
            minWidth: this.width,
            maxWidth: this.maxWidth,
            maxHeight: topBottomPadding * 2 + elementHeight * 9,
            items: this.menuItems,
            functions: {
                closeCallback: () => {
                    this.onCloseContextMenu();
                },
                onOutsideClick: (event) => {
                    if (!rect.contains(event.x, event.y)) {
                        this.HideMenu();
                    }
                }
            }
        });
        this.gotFocus();
    }
    updateMenuItems(selectedItem) {
        const items = this.get('items');
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (isNullOrUndefined(item.tag)) {
                item.tag = item.value;
            }
            if (isNullOrUndefined(item.event)) {
                item.event = this.private_OnMenuItemSelected;
            }
        }
        this.updateSelection(selectedItem);
        return items;
    }
    updateSelection(selectedItem) {
        const items = this.get('items');
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            item.selected = item === selectedItem;
        }
    }
    //TODO Autohide
    getContextMenuItem() {
        const item = super.getContextMenuItem();
        item.subitems = this.menuItems;
        return item;
    }
    lostFocus() {
        super.lostFocus();
        if (this.get('isMenuShown')) {
            this.HideMenu();
        }
    }
    HideMenu() {
        hideContextMenu();
        this.onCloseContextMenu();
    }
    onCloseContextMenu() {
        void this.set('isMenuShown', false);
        this.lostFocus();
    }
    private_OnMenuItemSelected(menuItem) {
        const newSelectedItem = this.getItemByValue(menuItem.tag);
        void this.set('selectedItem', newSelectedItem);
        this.fire(TerceraComboBoxEvents.ComboItemClicked, {}, menuItem);
    }
    forcedChangeMenuItemSelected(tag) {
        const newSelectedItem = this.getItemByValue(tag);
        void this.set('selectedItem', newSelectedItem);
    }
    addItem(newItem) {
        const myItems = this.get('items');
        myItems.push(newItem);
        void this.set({ items: myItems });
    }
    addItemRange(newItems) {
        let myItems = this.get('items');
        myItems = myItems.concat(newItems);
        void this.set({ items: myItems });
    }
    removeItem(itemIndex) {
        const myItems = this.get('items');
        myItems.splice(itemIndex, 1);
        void this.set({ items: myItems });
    }
    setItembyValue(value) {
        const myItems = this.get('items');
        if (!myItems)
            return;
        const len = myItems.length;
        for (let i = 0; i < len; i++) {
            if (myItems[i].value === value) {
                void this.set('selectedItem', myItems[i]);
            }
        }
    }
    count() {
        const items = this.get('items');
        return items ? items.length : 0;
    }
    clearItems() {
        void this.set('items', []);
    }
    clearSelection() {
        void this.set({
            selectedItem: {
                value: '',
                text: '',
                style: ''
            }
        });
    }
    onLocaleChanged() {
        this.update('selectedItemLocalizedText');
    }
}
Control.extendWith(TerceraComboBox, {
    template: TerceraComboBoxTemplate,
    data: function () {
        return {
            isMenuShown: false,
            showLastValue: false,
            items: [],
            selectedItem: null,
            tooltip: '',
            elementStyle: 'js-comboBox',
            additionalClass: '',
            listWidth: 0,
            hasAdaptiveMenu: false,
            useSVGView: false,
            usePNGView: false,
            showArrow: true,
            canHaveNoSelectedItem: false,
            visibleInfoBtn: false,
            infoBtnTooltip: '',
            updateComboBoxClass: '', // Развитие возможно всегда. Идеал — цель, которая всегда меняется. Эволюция никогда не останавливается. Нельзя останавливаться!
            isPosAbsolute: false
        };
    },
    computed: {
        selectedItemLocalizedText: {
            get: function () {
                const selectedItem = this.get('selectedItem');
                if (!isNullOrUndefined(selectedItem) && isValidString(selectedItem.text)) {
                    let text = Resources.getResource(selectedItem.text);
                    if (isValidString(selectedItem.additionalText)) {
                        text += ' ' + selectedItem.additionalText;
                    }
                    return text;
                }
                else {
                    return '';
                }
            }
        }
    }
});
// Events
export var TerceraComboBoxEvents;
(function (TerceraComboBoxEvents) {
    TerceraComboBoxEvents["ValueChange"] = "ValueChange";
    TerceraComboBoxEvents["ComboItemClicked"] = "ComboItemClicked";
})(TerceraComboBoxEvents || (TerceraComboBoxEvents = {}));
export class MenuItem {
    constructor(text, value, style) {
        this.text = text;
        this.value = value;
        this.style = style;
    }
}
